/**
 * Valuation slideover: one new-business lead, opened from the property's
 * Lettings list, the Valuations page or Today. Holds the pipeline form that
 * used to live on the property slideover's Valuation tab.
 */

import { pill } from '../utils/pills'

export function valuationSlideover() {
  return {
    open: false,
    loading: false,
    error: null,
    valuation: null,
    valuationId: null,
    zIndex: 100010,

    form: {},
    saving: false,
    converting: false,
    formError: null,
    summary: null,
    summaryMeta: { emails: 0, records: 0 },
    summarising: false,

    init() {
      // The property slideover refreshes when a lead changes; so does this panel
      window.addEventListener('valuation-updated', (e) => {
        if (this.open && this.valuation && e.detail?.id === this.valuation.id && e.detail?.data) {
          this.valuation = e.detail.data
          this.initForm()
        }
      })
    },

    openForValuation(id) {
      const store = Alpine.store?.('slideovers')
      if (store?.isOpen?.('valuation', id) && this.open && this.valuationId === id) {
        store.bringToFront('valuation', id)
        return
      }
      if (store?.isOpen?.('valuation', id)) store.unregister('valuation', id)
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex()

      this.valuationId = id
      this.valuation = null
      this.error = null
      this.summary = null
      this.formError = null
      this.open = true
      if (store?.register) store.register('valuation', id, this)
      this.fetchValuation()
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store && this.valuationId) store.unregister('valuation', this.valuationId)
      this.open = false
    },

    destroy() {
      const store = Alpine.store?.('slideovers')
      if (store && this.valuationId) store.unregister('valuation', this.valuationId)
    },

    async fetchValuation() {
      if (!this.valuationId) return
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`/valuations/${this.valuationId}.json`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.valuation = await response.json()
        this.initForm()
      } catch (e) {
        console.error('[ValuationSlideover] Failed to fetch valuation:', e)
        this.error = 'Could not load this valuation.'
      } finally {
        this.loading = false
      }
    },

    initForm() {
      const v = this.valuation
      this.formError = null
      if (!v) { this.form = {}; return }
      this.form = {
        status: v.status,
        lost_reason: v.lost_reason || '',
        valuation_at: this.toLocalInput(v.valuation_at),
        appraised_rent: v.appraised_rent,
        proposed_service_type: v.proposed_service_type || '',
        follow_up_on: v.follow_up_on || '',
        notes: v.notes || ''
      }
    },

    // ISO8601 → "YYYY-MM-DDTHH:mm" for <input type=datetime-local>
    toLocalInput(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d)) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    headers() {
      return { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
    },

    async save() {
      const v = this.valuation
      if (!v || this.saving) return
      this.saving = true
      this.formError = null
      try {
        const payload = { ...this.form }
        if (payload.status !== 'lost') payload.lost_reason = ''
        const response = await fetch(`/valuations/${v.id}`, { method: 'PATCH', headers: this.headers(), body: JSON.stringify({ valuation: payload }) })
        const data = await response.json()
        if (response.ok) {
          this.valuation = data
          this.initForm()
          window.dispatchEvent(new CustomEvent('valuation-updated', { detail: { id: v.id, data } }))
        } else {
          this.formError = (data.errors || ['Could not save.']).join(', ')
        }
      } catch (e) {
        this.formError = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    },

    async convert() {
      const v = this.valuation
      if (!v || this.converting) return
      if (!confirm('Convert this valuation into an instruction? This creates a draft instruction for the property.')) return
      this.converting = true
      this.formError = null
      try {
        const response = await fetch(`/valuations/${v.id}/convert`, { method: 'PATCH', headers: this.headers() })
        const data = await response.json()
        if (response.ok) {
          this.valuation = data
          this.initForm()
          window.dispatchEvent(new CustomEvent('valuation-updated', { detail: { id: v.id, data } }))
        } else {
          this.formError = (data.errors || ['Could not convert.']).join(', ')
        }
      } catch (e) {
        this.formError = 'An unexpected error occurred. Please try again.'
      } finally {
        this.converting = false
      }
    },

    // Summary, on demand: one model call over the lead, its notes, calls and
    // the landlord's emails. Never stored.
    async summarise() {
      const v = this.valuation
      if (!v || this.summarising) return
      this.summary = null
      this.summarising = true
      this.formError = null
      try {
        const response = await fetch(`/valuations/${v.id}/summarise`, { method: 'POST', headers: this.headers() })
        const data = await response.json().catch(() => ({}))
        if (response.ok && data.success) {
          this.summary = data.sections
          this.summaryMeta = { emails: data.email_count, records: data.record_count }
        } else {
          this.formError = data.error || 'Could not summarise this valuation'
        }
      } catch (e) {
        console.error('Summarise failed:', e)
        this.formError = 'Could not summarise this valuation'
      } finally {
        this.summarising = false
      }
    },

    statusPill() {
      return pill({ won: 'green', lost: 'grey', requested: 'blue', booked: 'amber' }[this.valuation?.status] || 'blue')
    },

    formatCurrency(amount) {
      if (amount == null) return '—'
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount)
    }
  }
}
